import React, { useEffect, useMemo, useState } from 'react';
import { Film, Download, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { mccGet, mccDownloadUrl } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, EmptyState, StatusPill, PlatformIcon } from './mccUi';
import { humanDateTime } from './mccConstants';
import MccInlineFootageUploader from './MccInlineFootageUploader';

/**
 * Raw Footage list — dark cinematic panel. Editors download raw files here.
 * Also allows direct upload of raw video/audio/image against any project.
 */
export default function MccRawFootage({ team, onOpenProject, onNav }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState('all');
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try {
      const [{ items: files }, { items: projs }] = await Promise.all([
        mccGet('/marketing/uploads'),
        mccGet('/marketing/projects'),
      ]);
      setItems(files || []);
      setProjects(projs || []);
    } catch (e) { toast.error(e.message || 'Failed to load raw footage'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  const rawKinds = new Set(['raw_video', 'raw_audio', 'raw_image']);
  const filtered = useMemo(() => {
    let list = items.filter((f) => rawKinds.has(f.kind));
    if (filterKind !== 'all') list = list.filter((f) => f.kind === filterKind);
    return list;
  }, [items, filterKind]);

  const grouped = useMemo(() => {
    const map = {};
    for (const f of filtered) {
      const key = f.project_id || 'unassigned';
      (map[key] = map[key] || []).push(f);
    }
    return map;
  }, [filtered]);

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Raw Footage"
        subtitle="Upload raw video, audio and image files here. Editors pick them up and produce edited versions."
        icon={Film}
        right={
          <Select value={filterKind} onValueChange={setFilterKind}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All raw</SelectItem>
              <SelectItem value="raw_video">Video</SelectItem>
              <SelectItem value="raw_audio">Audio</SelectItem>
              <SelectItem value="raw_image">Image</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {/* Upload panel — drop footage directly, no need to open a project first. */}
      <MccInlineFootageUploader
        mode="raw"
        title="Upload raw footage"
        hint="Choose a project and file kind, then drop the files. They&#39;ll appear below and in the project&#39;s Files tab."
        defaultKind="raw_video"
        onNav={onNav}
        onUploaded={load}
      />

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Film} title="No raw footage uploaded yet"
          body="Use the panel above to drop raw video, audio or image files. They'll appear here grouped by project and stay downloadable for the editing team." />
      ) : (
        Object.entries(grouped).map(([pid, files]) => {
          const proj = projectById[pid];
          return (
            <div key={pid} className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950 text-slate-100">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-3 flex-wrap">
                <Film className="w-5 h-5 text-indigo-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{proj ? proj.title : 'Unassigned raw files'}</p>
                  {proj ? (
                    <div className="flex items-center gap-2 mt-1">
                      <StatusPill status={proj.status} size="sm" />
                      {proj.primary_platform ? <PlatformIcon platform={proj.primary_platform} size={16} /> : null}
                      {(proj.additional_platforms || []).slice(0, 3).map((p) => <PlatformIcon key={p} platform={p} size={16} />)}
                    </div>
                  ) : null}
                </div>
                {proj && (
                  <Button variant="ghost" size="sm" onClick={() => onOpenProject(proj.id)} className="text-slate-300 hover:text-white hover:bg-slate-800">
                    Open project <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
              <div className="divide-y divide-slate-800">
                {files.map((f) => (
                  <div key={f.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-900/50">
                    <Film className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{f.original_filename}</p>
                      <p className="text-[11px] text-slate-400">
                        {f.kind.replace(/_/g, ' ')} · {(f.size / (1024 * 1024)).toFixed(1)} MB · uploaded by {f.uploaded_by_name} · {humanDateTime(f.created_at)}
                      </p>
                      {(f.downloaded_by || []).length > 0 && (
                        <p className="text-[10px] text-emerald-400 mt-0.5">
                          Downloaded by {(f.downloaded_by || []).map((d) => d.user_name).join(', ')}
                        </p>
                      )}
                    </div>
                    <a
                      href={mccDownloadUrl(f.id)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-indigo-500 hover:bg-indigo-700 text-white text-xs font-semibold"
                      data-testid={`mcc-raw-download-${f.id}`}
                    ><Download className="w-3 h-3" /> Download</a>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
